import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import ExportButton from './ExportButton';

export type LeaderboardDeltaSeverity = 'positive' | 'negative' | 'new' | 'neutral';

export interface LeaderboardDelta {
    label: React.ReactNode;
    severity: LeaderboardDeltaSeverity;
}

export interface LeaderboardItem {
    id: string | number;
    primary: React.ReactNode;        // main label (client name, stage name)
    secondary?: React.ReactNode;     // sub label (city, etc.)
    value: number;                   // raw value, used to compute bar width
    valueDisplay: React.ReactNode;   // formatted value shown on the row
    meta?: React.ReactNode;          // small inline meta (e.g., "5 agreements")
    delta?: LeaderboardDelta;        // optional per-row vs-previous pill
}

interface Props {
    title: React.ReactNode;
    subtitle?: React.ReactNode;
    items: LeaderboardItem[] | null;
    loading: boolean;
    accent?: 'primary' | 'warning';
    emptyMessage?: React.ReactNode;
    /** Rows shown by default; "Show all (N)" reveals up to `maxRows`. */
    defaultRows?: number;
    /** Hard upper bound — clicking "Show all" never reveals more than this. */
    maxRows?: number;
    onExport?: () => void;
}

const LeaderboardCard: React.FC<Props> = ({
    title,
    subtitle,
    items,
    loading,
    accent = 'primary',
    emptyMessage,
    defaultRows = 8,
    maxRows = 10,
    onExport,
}) => {
    const [expanded, setExpanded] = useState(false);
    return (
        <section className="av2-card">
            <div className="av2-card__header">
                <div className="av2-card__title-wrap">
                    <h3 className="av2-card__title">{title}</h3>
                    {subtitle && <p className="av2-card__subtitle">{subtitle}</p>}
                </div>
                {onExport && (
                    <ExportButton onClick={onExport} disabled={!items || items.length === 0} />
                )}
            </div>
            <div className="av2-card__body">
                {loading || !items ? (
                    <LeaderboardSkeleton count={5} />
                ) : items.length === 0 ? (
                    <div className="av2-empty">
                        {emptyMessage || (
                            <FormattedMessage id="analyticsV2.leaderboard.empty" defaultMessage="No data yet." />
                        )}
                    </div>
                ) : (
                    <>
                        <LeaderboardList
                            items={items.slice(0, expanded ? maxRows : defaultRows)}
                            accent={accent}
                        />
                        {items.length > defaultRows && (
                            <button
                                type="button"
                                className={`av2-show-all${expanded ? ' av2-show-all--expanded' : ''}`}
                                onClick={() => setExpanded((v) => !v)}
                            >
                                {expanded ? (
                                    <FormattedMessage
                                        id="analyticsV2.showLess"
                                        defaultMessage="Show less"
                                    />
                                ) : (
                                    <FormattedMessage
                                        id="analyticsV2.showAll"
                                        defaultMessage="Show all ({count})"
                                        values={{ count: Math.min(items.length, maxRows) }}
                                    />
                                )}
                                <span className="av2-show-all__chevron" aria-hidden="true">
                                    ⌄
                                </span>
                            </button>
                        )}
                    </>
                )}
            </div>
        </section>
    );
};

const LeaderboardList: React.FC<{ items: LeaderboardItem[]; accent: 'primary' | 'warning' }> = ({
    items,
    accent,
}) => {
    const max = Math.max(...items.map((i) => i.value), 0) || 1;

    return (
        <ol className="av2-leaderboard">
            {items.map((item, idx) => {
                const pct = (item.value / max) * 100;
                return (
                    <li key={item.id} className="av2-leaderboard__row" style={{ animationDelay: `${idx * 40}ms` }}>
                        <div className="av2-leaderboard__header">
                            <span className="av2-leaderboard__rank">{idx + 1}</span>
                            <div className="av2-leaderboard__label">
                                <span className="av2-leaderboard__name">{item.primary}</span>
                                {item.secondary && <span className="av2-leaderboard__sub">{item.secondary}</span>}
                            </div>
                            <div className="av2-leaderboard__rhs">
                                <span className="av2-leaderboard__value">{item.valueDisplay}</span>
                                {item.delta && (
                                    <span
                                        className={`av2-leaderboard__delta av2-leaderboard__delta--${item.delta.severity}`}
                                    >
                                        {item.delta.label}
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="av2-leaderboard__bar">
                            <span
                                className={`av2-leaderboard__bar-fill av2-leaderboard__bar-fill--${accent}`}
                                style={{ width: `${pct}%` }}
                            />
                        </div>
                        {item.meta && <div className="av2-leaderboard__meta">{item.meta}</div>}
                    </li>
                );
            })}
        </ol>
    );
};

const LeaderboardSkeleton: React.FC<{ count: number }> = ({ count }) => {
    return (
        <ol className="av2-leaderboard">
            {Array.from({ length: count }, (_, i) => (
                <li key={i} className="av2-leaderboard__row av2-leaderboard__row--skeleton">
                    <div className="av2-leaderboard__header">
                        <div className="av2-skel av2-skel--rank" />
                        <div className="av2-skel av2-skel--lb-label" />
                        <div className="av2-skel av2-skel--lb-value" />
                    </div>
                    <div className="av2-skel av2-skel--lb-bar" />
                </li>
            ))}
        </ol>
    );
};

export default LeaderboardCard;
